/**
 * The catalogue, in the URL.
 *
 * ClassCalc's one deep-linkable choice, and deliberately the only one. The rest
 * of the form is somebody's actual car — weight, power, a timeslip — and a URL
 * carrying that would turn a page that stores nothing into a page that hands the
 * whole combination to every referrer header, proxy log and pasted link on the
 * way. §3's non-goals are kept by there being no code that could break them, so
 * the query string stays empty and the path carries exactly one thing.
 *
 * The catalogue is worth carrying because it is not private and it *is* the
 * question people arrive with: a Super Comp racer and a Drag Week entrant are
 * looking up different rulebooks, and each deserves a link they can send to a
 * crew chief and a search result that says which one they are getting.
 *
 * Four catalogues, four URLs, one apiece:
 *
 *   /tools/classcalc                      every catalogue — the default
 *   /tools/classcalc/nhra-ihra            NHRA and IHRA sportsman classes
 *   /tools/classcalc/drag-week-sick-week  the drag-and-drive fields
 *   /tools/classcalc/radial               X275, Ultra Street, LDR, RvW
 *
 * The default sits at the bare path rather than a slug of its own because that
 * URL is the one already in `sitemap.xml`, in the footer and on the landing
 * page, and moving it would trade indexed history for tidiness. `/all` and the
 * older `/both` are both accepted — they are the obvious things to type, and
 * `/both` was live while there were only two narrowed catalogues — but they
 * redirect rather than render, so no catalogue is ever reachable at two
 * addresses for a crawler to weigh against each other.
 */

import { DEFAULT_INTEREST, INTERESTS } from './spec';
import type { Interest } from './spec';

export const CLASSCALC_PATH = '/tools/classcalc';

/**
 * Catalogue → slug.
 *
 * Spelled out rather than derived from the label. `'NHRA / IHRA'.toLowerCase()`
 * with the spaces squeezed out would give the same three strings today, but it
 * would also mean that renaming a button — a copy change, made by someone
 * thinking about a button — silently breaks every link anyone has shared.
 */
const SLUGS: Record<Interest, string> = {
  'NHRA / IHRA': 'nhra-ihra',
  'Drag Week / Sick Week': 'drag-week-sick-week',
  'Radial classes': 'radial',
  All: 'all',
};

/**
 * Slugs that are not the canonical name of a catalogue but still resolve to one.
 *
 * `both` was the default's slug back when there were two narrowed catalogues and
 * "Both" was an accurate name for the third button. Adding the radial catalogue
 * made it "All", and a slug that shipped once is a slug somebody has in a text
 * message — it keeps working, and lands where it always did.
 */
const ALIASES: Record<string, Interest> = { both: 'All' };

/**
 * The path a catalogue is served at — which for the default is the bare one.
 *
 * This is also the canonical URL for that catalogue, so it is what the page
 * hands `usePageMeta`, and what the catalogue buttons navigate to.
 */
export function pathForInterest(interest: Interest): string {
  return interest === DEFAULT_INTEREST ? CLASSCALC_PATH : `${CLASSCALC_PATH}/${SLUGS[interest]}`;
}

/**
 * A path segment back to a catalogue, or null when it names nothing.
 *
 * No segment at all is the default catalogue rather than a miss — the bare path
 * is a real URL, not a mistake. Case is forgiven because a link that came back
 * through a mail client or a forum's link-shortener may not arrive the way it
 * left; the caller sends anything non-canonical on to the canonical URL, so
 * `/NHRA-IHRA` works without also becoming a second address for the same page.
 */
export function interestFromSlug(slug: string | undefined): Interest | null {
  if (slug === undefined) return DEFAULT_INTEREST;
  const wanted = slug.trim().toLowerCase();
  return INTERESTS.find((interest) => SLUGS[interest] === wanted) ?? ALIASES[wanted] ?? null;
}

/**
 * What each of the three pages calls itself.
 *
 * The point of a deep link is that it lands somewhere that looks answered
 * rather than somewhere with a toggle in the right position, so the title, the
 * search-result line and the opening paragraph all name the catalogue you asked
 * for. Two of these are otherwise duplicates in a crawler's eyes: same shell,
 * same form, same checklist.
 */
export interface CataloguePage {
  title: string;
  description: string;
  lede: string;
}

export const CATALOGUE_PAGE: Record<Interest, CataloguePage> = {
  All: {
    title: 'Drag racing class calculator — NHRA, IHRA, Drag Week, Sick Week & radial | SvcDue',
    description:
      'Enter your combination and see which NHRA, IHRA, Hot Rod Drag Week, Sick Week or radial-tire classes fit it, plus the minimum safety equipment the rulebooks call for at that pace and on that fuel. Free, no sign-in, nothing stored.',
    lede: 'Covers NHRA and IHRA sportsman classes, the drag-and-drive fields at Hot Rod Drag Week and Sick Week, and the radial-tire classes.',
  },
  'Radial classes': {
    title: 'Radial class calculator — X275, Ultra Street, LDR & Radial vs the World | SvcDue',
    description:
      'Enter your combination and see which radial-tire classes fit it — X275, Ultra Street, Limited Drag Radial, Radial vs the World — with the tire, frame and suspension rules each one is written on. Free, no sign-in, nothing stored.',
    lede: 'Covers the radial-tire heads-up classes, where the tire is the class and the frame and suspension rules follow from it.',
  },
  'NHRA / IHRA': {
    title: 'NHRA & IHRA class calculator — sportsman classes and safety rules | SvcDue',
    description:
      'Enter your combination and see which NHRA and IHRA sportsman classes fit it — Super Comp, Super Gas, Stock, the bracket ladders — plus the minimum safety equipment the rulebooks call for at that pace. Free, no sign-in, nothing stored.',
    lede: 'Covers the NHRA and IHRA sportsman classes, selected by ET.',
  },
  'Drag Week / Sick Week': {
    title: 'Drag Week & Sick Week class calculator — drag-and-drive classes | SvcDue',
    description:
      'Enter your combination and see which Hot Rod Drag Week and Sick Week classes fit it — tread width, engine family, how the back half is built — plus the minimum safety equipment those rulebooks call for at that pace. Free, no sign-in, nothing stored.',
    lede: 'Covers the drag-and-drive fields at Hot Rod Drag Week and Sick Week, which turn on how the car is built rather than on ET alone.',
  },
};
